import { useMemo } from "react";

import { type Chart, type ChartDataset, type ChartOptions } from "chart.js";
import { motion } from "framer-motion";
import { sum } from "lodash";
import moment from "moment";
import { Line } from "react-chartjs-2";

import { LoadingSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { NB_COLOR } from "@features/mmm/components/explore-budgets/budget-mix-chart/color-helper";
import {
  type Kpi,
  KpiType,
} from "@features/mmm/components/explore-budgets/client-setting/types";
import { type WeeklyDatum } from "@features/mmm/components/explore-budgets/scenario-modeling/types";
import {
  dailyToWeeklyData,
  getMostRecentPastDay,
  Weekday,
} from "@features/mmm/utils/date-stretch-compress-helpers";
import { getDate } from "@features/mmm/utils/formatters";
import { getWeeksForDateRange } from "@features/mmm/utils/get_cycle_weeks";
import { drawAlternativeMetricTitle } from "@features/sales-v3/utils/canvas";
import { getLighterColor } from "@hooks/use-design-element-manager/design-element-utils";
import { MetricFormatters } from "@north-beam/nb-client-side";
import { type WeeklyBudgetMixDatum } from "@north-beam/nb-common";

import { chartTooltip } from "./tooltip";

interface DataPoint {
  x: string;
  y: number;
}

function convertToLabel(date: string) {
  const start = moment(date);
  const startFormatted = start.isValid() ? start.format("MMM DD") : "Start";
  return startFormatted;
}

const PRIMARY_COLOR = "#7081FF";

type BudgetMixWeeklyData = Omit<WeeklyBudgetMixDatum, "spend_data">[];
type BudgetMixInputWeeklyData = Omit<WeeklyDatum, "spend_data">[];

interface Props {
  selectedKpi: Kpi;
  actualDailyKpiData?: { date: string; actual: number | null }[];
  adoptedMixWeeklyValues?: BudgetMixWeeklyData;
  forecastedMixWeeklyValues?: BudgetMixInputWeeklyData;
  cycleDateRange: { start_date: string; end_date: string };
  loading: boolean;
  height: number;
}

export const CurrentForecastHybridChart = ({
  selectedKpi,
  actualDailyKpiData,
  adoptedMixWeeklyValues,
  forecastedMixWeeklyValues,
  loading,
  cycleDateRange,
  height,
}: Props) => {
  const { user } = useUser();

  const shouldRenderActual = Boolean(actualDailyKpiData);
  const shouldRenderForecast = Boolean(forecastedMixWeeklyValues);
  const shouldRenderAdopted = Boolean(adoptedMixWeeklyValues);

  const chartData = useMemo(() => {
    if (
      !actualDailyKpiData &&
      !adoptedMixWeeklyValues &&
      !forecastedMixWeeklyValues
    ) {
      return { labels: [], datasets: [] };
    }
    const startOfCurrentWeek = getMostRecentPastDay(
      new Date(),
      Weekday.TUESDAY,
    );

    const actualDailyKpiDataArray = actualDailyKpiData ?? [];
    const forecastedMixWeeklyValuesArray = forecastedMixWeeklyValues ?? [];
    const adoptedMixWeeklyValuesArray = adoptedMixWeeklyValues ?? [];

    const formattedDailyKpiData = actualDailyKpiDataArray.map((datum) => ({
      date: datum.date,
      data: datum.actual ?? 0,
    }));
    const weeklyDataTransformer = (
      data: number[],
      _startDate: string,
      _endDate: string,
    ) => sum(data);

    const weeklyActualKpiData = dailyToWeeklyData(
      formattedDailyKpiData,
      weeklyDataTransformer,
    );

    const labels = getWeeksForDateRange(cycleDateRange).map((week) =>
      convertToLabel(week.startDate),
    );
    const datasets: ChartDataset<"line", DataPoint[]>[] = [];

    const actualKpiDataset: DataPoint[] = weeklyActualKpiData
      .filter((datum) => getDate(datum.date) <= getDate(startOfCurrentWeek))
      .map((datum) => ({
        x: convertToLabel(datum.date),
        y: datum.data,
      }));

    const adoptedKpiForecastDataset: DataPoint[] = adoptedMixWeeklyValuesArray
      .filter(
        (datum) => getDate(datum.start_date) >= getDate(startOfCurrentWeek),
      )
      .map((datum) => ({
        x: convertToLabel(datum.start_date),
        y: datum.summary_data.kpi_data[selectedKpi.kpi_name].values.median,
      }));

    const newKpiForecastDataset: DataPoint[] = forecastedMixWeeklyValuesArray
      .filter(
        (datum) => getDate(datum.start_date) >= getDate(startOfCurrentWeek),
      )
      .map((datum) => ({
        x: convertToLabel(datum.start_date),
        y: datum.summary_data.kpi_data[selectedKpi.kpi_name].values.median,
      }));
    const newKpiForecastLowDataset: DataPoint[] = forecastedMixWeeklyValuesArray
      .filter(
        (datum) => getDate(datum.start_date) >= getDate(startOfCurrentWeek),
      )
      .map((datum) => ({
        x: convertToLabel(datum.start_date),
        y: datum.summary_data.kpi_data[selectedKpi.kpi_name].values.low,
      }));
    const newKpiForecastHighDataset: DataPoint[] =
      forecastedMixWeeklyValuesArray
        .filter(
          (datum) => getDate(datum.start_date) >= getDate(startOfCurrentWeek),
        )
        .map((datum) => ({
          x: convertToLabel(datum.start_date),
          y: datum.summary_data.kpi_data[selectedKpi.kpi_name].values.high,
        }));

    datasets.push(
      {
        data: actualKpiDataset,
        label: selectedKpi.friendly_name,
        series: "Actual",
        yAxisId: "y",
        animation: false,
        pointRadius: 0,
        pointHitRadius: 25,
        hoverBackgroundColor: NB_COLOR.ALIEN,
        borderColor: NB_COLOR.ALIEN,
        hoverBorderColor: NB_COLOR.ALIEN,
        borderWidth: 2,
        tension: 0.4,
      } as ChartDataset<"line", DataPoint[]>,
      {
        data: adoptedKpiForecastDataset,
        label: selectedKpi.friendly_name,
        series: "Adopted",
        yAxisID: "y",
        animation: false,
        pointRadius: 0,
        pointHitRadius: 25,
        hoverBackgroundColor: NB_COLOR.JUPITER,
        borderColor: NB_COLOR.JUPITER,
        hoverBorderColor: NB_COLOR.JUPITER,
        borderWidth: 2,
        borderDash: [6, 2],
      } as ChartDataset<"line", DataPoint[]>,
      {
        data: newKpiForecastDataset,
        label: selectedKpi.friendly_name,
        series: "Forecast",
        yAxisID: "y",
        animation: false,
        pointRadius: 0,
        pointHitRadius: 25,
        hoverBackgroundColor: PRIMARY_COLOR,
        borderColor: PRIMARY_COLOR,
        hoverBorderColor: PRIMARY_COLOR,
        borderWidth: 2,
      } as ChartDataset<"line", DataPoint[]>,
      {
        label: "Low",
        series: "Forecast",
        data: newKpiForecastLowDataset,
        backgroundColor: getLighterColor(PRIMARY_COLOR, "0.11"),
        fill: "+1",
        yAxisID: "y",
        pointRadius: 0,
        borderWidth: 0,
        tension: 0,
        animation: false,
      } as ChartDataset<"line", DataPoint[]>,
      {
        label: "High",
        series: "Forecast",
        data: newKpiForecastHighDataset,
        yAxisID: "y",
        pointRadius: 0,
        borderWidth: 0,
        tension: 0,
        animation: false,
      } as ChartDataset<"line", DataPoint[]>,
    );

    // if (forecastSpendData) {
    //   const spendData = forecastSpendData.map((datedSpend) => {
    //     return {
    //       x: convertToLabel(datedSpend.date),
    //       y: sum(Object.values(datedSpend.data)),
    //     } as DataPoint;
    //   });

    //   datasets.push({
    //     data: spendData,
    //     label: "Spend",
    //     series: "Forecast",
    //     yAxisID: "y1",
    //     animation: false,
    //     pointRadius: 0,
    //     pointHitRadius: 50,
    //     hoverBackgroundColor: PRIMARY_COLOR,
    //     borderColor: PRIMARY_COLOR,
    //     hoverBorderColor: PRIMARY_COLOR,
    //     borderDash: [6, 2],
    //   } as ChartDataset<"line", DataPoint[]>);
    // }

    return { labels, datasets };
  }, [
    selectedKpi,
    forecastedMixWeeklyValues,
    adoptedMixWeeklyValues,
    actualDailyKpiData,
    cycleDateRange,
  ]);

  const isRevenue = selectedKpi.kpi_type === KpiType.REVENUE;

  const isLoading = loading;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {isLoading ? (
        <div style={{ height: `${height}px` }}>
          <LoadingSlide text="Loading your KPI data" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-center gap-6 text-xxs font-bold">
            {shouldRenderForecast && (
              <>
                <label className="flex items-center gap-2 cursor-pointer">
                  <div className="w-6 h-0.5 bg-cosmic"></div>
                  New budget
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <div className="h-4 w-4 bg-cosmic-3"></div>
                  Range
                </label>
              </>
            )}
            {shouldRenderAdopted && (
              <label className="flex items-center gap-2 cursor-pointer">
                <div className="w-6 h-0.5 bg-jupiter"></div>
                Adopted budget
              </label>
            )}
            {shouldRenderActual && (
              <label className="flex items-center gap-2 cursor-pointer">
                <div className="w-6 h-0.5 bg-alien"></div>
                Actual
              </label>
            )}
          </div>
          <div>
            <Line
              datasetIdKey="id"
              height={height ?? 400}
              plugins={[
                {
                  id: "forecastChartYScaleTitles",
                  afterDraw(
                    chart: Chart,
                    _args: any,
                    options: { mainScaleTitle: string },
                  ) {
                    const {
                      ctx,
                      chartArea,
                      scales: { y1 },
                    } = chart;
                    drawAlternativeMetricTitle(ctx, options.mainScaleTitle, {
                      font: {
                        family: "Inter",
                        size: 12,
                        weight: "600",
                        style: "normal",
                        lineHeight: 16,
                      },
                      x: 8,
                      tickWidth: 12,
                      chart,
                      chartArea,
                      config: {
                        baseBackgroundColor: PRIMARY_COLOR,
                        scaleBackgroundColor: "#f2f3f5",
                      },
                    });
                  },
                },
              ]}
              options={
                {
                  responsive: true,
                  maintainAspectRatio: false,
                  parsing: false,
                  interaction: {
                    mode: "x",
                    intersect: false,
                    position: "nearest",
                  },
                  plugins: {
                    forecastChartYScaleTitles: {
                      mainScaleTitle: selectedKpi.friendly_name.toUpperCase(),
                    },
                    tooltip: {
                      enabled: false,
                      external: chartTooltip,
                      user,
                      isRevenue,
                      position: "nearest",
                      xAlign: "right",
                      yAlign: "bottom",
                    },
                    legend: { display: false },
                  },
                  layout: {
                    padding: { left: 8, right: 20 },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: {
                        drawTicks: false,
                        drawBorder: false,
                      },
                      ticks: {
                        maxTicksLimit: 6,
                        padding: 18,
                        font: { size: 11 },
                        callback: (val: string | number) => {
                          if (selectedKpi.kpi_type === "price_value") {
                            return MetricFormatters.currency(
                              Number(val),
                              user.currencies.currentCurrency,
                              { precision: "approximate" },
                            );
                          } else {
                            return MetricFormatters.decimal(
                              Number(val),
                              user.currencies.currentCurrency,
                              { precision: "approximate" },
                            );
                          }
                        },
                      },
                    },
                    x: {
                      grid: {
                        drawBorder: false,
                        drawTicks: false,
                        drawOnChartArea: false,
                      },
                      ticks: {
                        padding: 18,
                        autoSkip: true,
                        font: { size: 11 },
                      },
                    },
                  },
                } as ChartOptions<"line">
              }
              data={chartData}
            />
          </div>
        </>
      )}
    </motion.div>
  );
};
